import React from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { FaHourglassHalf, FaBan, FaEnvelopeOpenText } from "react-icons/fa";

/**
 * What a staff applicant sees while their account is waiting, and what a
 * refused account sees instead.
 *
 * They hold a session but no privileges — the server resolves them to
 * 'customer', so every staff route refuses them. This screen exists so that
 * lands as an explanation rather than a series of empty pages.
 *
 * By agreement nobody is emailed when they are approved or rejected, so the
 * only way to find out is to sign in again. The copy says so plainly rather
 * than leaving people waiting for a message that never arrives.
 */

type Reason = "pending" | "rejected" | "disabled";

const CONTENT: Record<Reason, {
    icon: React.ReactNode;
    title: string;
    body: string;
    tone: string;
}> = {
    pending: {
        icon: <FaHourglassHalf className="text-amber-500" size={38} />,
        title: "Your account is waiting for approval",
        body:
            "Someone at head office needs to approve your account before you can start booking. " +
            "You will not receive an email when this happens, so please sign in again later to check.",
        tone: "bg-amber-50 border-amber-200",
    },
    rejected: {
        icon: <FaBan className="text-red-500" size={38} />,
        title: "Your account has not been approved",
        body:
            "Head office has not approved this account. If you think this is a mistake, speak to " +
            "your manager — they can approve it at any time.",
        tone: "bg-red-50 border-red-200",
    },
    disabled: {
        icon: <FaBan className="text-red-500" size={38} />,
        title: "Your account has been switched off",
        body:
            "This account has been disabled by head office. Speak to your manager if you believe " +
            "it should be switched back on.",
        tone: "bg-red-50 border-red-200",
    },
};

const AwaitingApproval: React.FC<{ reason?: Reason }> = ({ reason }) => {
    const navigate = useNavigate();
    const { user, profile } = useAuth();

    // Prefer what the profile actually says; fall back to the prop for the
    // refused cases, where there is no profile to read.
    const status = (profile?.staffStatus as Reason | undefined) ?? reason ?? "pending";
    const content = CONTENT[status] ?? CONTENT.pending;

    const handleSignOut = async () => {
        try {
            await signOut(auth);
        } finally {
            navigate("/login");
        }
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-4">
            <div className={`max-w-md w-full rounded-2xl border p-8 text-center ${content.tone}`}>
                <div className="flex justify-center mb-4">{content.icon}</div>

                <h1 className="text-xl font-semibold text-gray-900 mb-3">{content.title}</h1>
                <p className="text-sm text-gray-600 leading-relaxed mb-6">{content.body}</p>

                {user?.email && (
                    <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-6">
                        <FaEnvelopeOpenText />
                        <span>Signed in as {user.email}</span>
                    </div>
                )}

                <button
                    onClick={handleSignOut}
                    className="w-full bg-gray-900 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-gray-800 transition"
                >
                    Sign out
                </button>
            </div>
        </div>
    );
};

export default AwaitingApproval;
